'use client'

import { Product, ProductVariant } from '@repo/shopify/types'
import { useAnimate } from 'motion/react'
import { useSearchParams } from 'next/navigation'
import { createContext, ReactNode, RefObject, useContext, useMemo, useOptimistic, useRef } from 'react'

type ProductState = {
  [key: string]: string | null | undefined
}

type ProductStateUrlUpdate = {
  [key: string | number]: unknown
}

type Combination = {
  id: string
  availableForSale: boolean
  [key: string]: string | boolean
}

type ProductContextType = {
  state: ProductState
  galleryRef: RefObject<HTMLUListElement | null>
  addToCartRef: RefObject<HTMLDivElement | null>
  updateOption: (name: string, value: string) => ProductState
  getCombinations: (product?: Product) => Combination[]
  getSelectedVariant: (product?: Product) => ProductVariant | undefined
  extractColorNameFromShopifyProduct: (product: Product) => string
  updateLightboxMedia: (index: string | undefined) => ProductState
  updateCarouselMedia: (index: string) => ProductState
  goToAddToCartButton: (nudge: boolean | undefined) => void
}

const ProductContext = createContext<ProductContextType | undefined>(undefined)

export function ProductProvider({ children }: { children: ReactNode }) {
  const [addToCartRef, animate] = useAnimate()
  const searchParams = useSearchParams()
  const galleryRef = useRef<HTMLUListElement | null>(null)

  const getInitialState = (): ProductState => {
    const params: ProductState = {}

    for (const [key, value] of searchParams.entries()) {
      params[key] = value
    }

    return params
  }

  const [state, setOptimisticState] = useOptimistic(getInitialState(), (prevState: ProductState, update: ProductState) => {
    const newState = { ...prevState }

    for (const key in update) {
      if (update[key] === null || update[key] === undefined) {
        delete newState[key]
      } else {
        newState[key] = update[key]
      }
    }

    return newState
  })

  const updateOption = (name: string, value: string) => {
    const newState = { [name]: value }

    setOptimisticState(newState)

    return { ...state, ...newState }
  }

  const getCombinations = (product?: Product) => {
    return (
      product?.variants?.map((variant) => ({
        id: variant.id,
        availableForSale: variant.availableForSale,
        ...variant.selectedOptions.reduce((accumulator, option) => ({ ...accumulator, [option.name.toLowerCase()]: option.value }), {}),
      })) || []
    )
  }

  const getSelectedVariant = (product?: Product) => {
    // If an option (e.g. 'Color'), only has one possible value (e.g. 'Black'), the end-user should not be
    // required to select such option, and therefore it's not present in the URL state of the current page.
    const optionNamesThatRequireDecision = product?.options?.filter((option) => option.values.length > 1)?.map((option) => option.name) || []

    return product?.variants?.find((variant: ProductVariant) =>
      variant.selectedOptions.every((option) => {
        if (optionNamesThatRequireDecision.includes(option.name)) {
          return option.value === state[option.name.toLowerCase()]
        }

        return true
      }),
    )
  }

  const extractColorNameFromShopifyProduct = (product: Product) => {
    return product.options.find((option) => option.name.toLowerCase() === 'color')?.values?.[0] || product.title
  }

  const updateLightboxMedia = (index: string | undefined) => {
    const newState = { lightbox: index }

    setOptimisticState(newState)

    return { ...state, ...newState }
  }

  const updateCarouselMedia = (index: string) => {
    const newState = { media: index }

    setOptimisticState(newState)

    return { ...state, ...newState }
  }

  const goToAddToCartButton = (nudge = true) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

    if (nudge) {
      animate(addToCartRef.current, { y: [0, -5, 5, -5, 5, 0] }, { duration: 1, ease: 'easeInOut' })
    }
  }

  const value = useMemo(
    () => ({
      state,
      galleryRef,
      addToCartRef,
      updateOption,
      getCombinations,
      getSelectedVariant,
      extractColorNameFromShopifyProduct,
      updateLightboxMedia,
      updateCarouselMedia,
      goToAddToCartButton,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state],
  )

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
}

export function useProduct() {
  const context = useContext(ProductContext)

  if (context === undefined) {
    throw new Error('useProduct must be used within a ProductProvider')
  }

  return context
}

export function useUpdateURL() {
  // Current URL search params.
  const searchParams = useSearchParams()

  return (state: ProductStateUrlUpdate, distinct = false) => {
    const urlSearchParams = new URLSearchParams(searchParams.toString())

    Object.entries(state).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        urlSearchParams.delete(key)
      } else if (distinct && searchParams.get(key) === value) {
        // Act like a toggle, so remove URL param.
        urlSearchParams.delete(key)
      } else {
        urlSearchParams.set(key, String(value))
      }
    })

    window.history.replaceState(null, '', `?${urlSearchParams.toString()}`)
  }
}
